<template>
  <section class="import-panel">
    <div class="import-left">
      <div class="method-title">
        <el-tooltip :content="$t('QuotationCenter.components.excelImportTip')" placement="top-start">
          <span>!</span>
        </el-tooltip>
        {{ $t('QuotationCenter.components.excelImportMethod') }}
      </div>
      <div class="upload-actions">
        <el-button class="template-btn" icon="el-icon-download" @click="handleDownloadTemplate">{{ $t('QuotationCenter.components.importTemplate') }}</el-button>
        <el-button class="upload-btn-inline" type="primary" icon="el-icon-upload2" @click="openImportDialog">{{ $t('QuotationCenter.components.clickUpload') }}</el-button>
        <div class="upload-tip">
          <span>{{ $t('QuotationCenter.components.onlySupportExcel') }}</span>
        </div>
      </div>
    </div>

    <div class="sample-box">
      <h4>
        {{ $t('QuotationCenter.components.priceSample') }}
        <el-tooltip :content="$t('QuotationCenter.components.sampleData')" placement="top">
          <i class="el-icon-question"></i>
        </el-tooltip>
      </h4>
      <el-table ref="sampleTable" :data="sampleRows" border size="mini" class="sample-table">
        <el-table-column prop="index" :label="$t('QuotationCenter.serialNumber')" align="center" width="70" />
        <el-table-column prop="parcelType" :label="$t('QuotationCenter.packageType')" align="center" min-width="110" />
        <el-table-column prop="zone" :label="$t('QuotationCenter.zone')" align="center" width="80" />
        <el-table-column prop="startWeight" :label="$t('QuotationCenter.start')" align="center" width="80" />
        <el-table-column prop="endWeight" :label="$t('QuotationCenter.end')" align="center" width="80" />
        <el-table-column prop="calcType" :label="$t('QuotationCenter.calcMethod')" align="center" min-width="150" />
        <el-table-column prop="unitWeight" :label="$t('QuotationCenter.components.roundingUnit')" align="center" />
        <el-table-column prop="price" :label="$t('QuotationCenter.components.unitPrice')" align="center">
          <template slot-scope="scope">
            <span>{{ $number2money(scope.row.price, 4) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="minimumCharge" :label="$t('QuotationCenter.components.minimumCharge')" align="center" min-width="110">
          <template slot-scope="scope">
            <span>{{ $number2money(scope.row.minimumCharge) }}</span>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <QuotationImportDialog
      :visible.sync="importDialogVisible"
      :loading="loading"
      @closed="handleDialogClosed"
      @confirm="$emit('confirm', $event)"
    />
  </section>
</template>

<script>
import QuotationImportDialog from './QuotationImportDialog.vue';

const IMPORT_TEMPLATE_URL = 'https://minio.wooper.com/test/11/QuotationImportTemplate';

export default {
  name: 'QuotationImportPanel',
  components: {
    QuotationImportDialog,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    importable: {
      type: Boolean,
      default: true,
    },
    templateUrl: {
      type: String,
      default: IMPORT_TEMPLATE_URL,
    },
    unavailableMessageKey: {
      type: String,
      default: 'QuotationCenter.feeItem',
    },
  },
  data() {
    return {
      importDialogVisible: false,
    };
  },
  computed: {
    sampleRows() {
      return [
        { index: 1, parcelType: this.$t('Q_DocumentText'), zone: '1', startWeight: '0', endWeight: '2', calcType: this.$t('Q_LookupText'), unitWeight: '0', price: '18', minimumCharge: '20' },
        { index: 2, parcelType: this.$t('Q_PackageText'), zone: '1', startWeight: '0.05', endWeight: '0.2', calcType: this.$t('Q_IncrementalText'), unitWeight: '0', price: '18', minimumCharge: '20' },
        { index: 3, parcelType: this.$t('Q_BagText'), zone: '1', startWeight: '0.201', endWeight: '0.45', calcType: this.$t('Q_LookupText'), unitWeight: '0', price: '16', minimumCharge: '18' },
        { index: 4, parcelType: this.$t('Q_PackageText'), zone: '1', startWeight: '0.451', endWeight: '0.7', calcType: this.$t('Q_LookupText'), unitWeight: '0', price: '16', minimumCharge: '18' },
        { index: 5, parcelType: '--', zone: '--', startWeight: '--', endWeight: '--', calcType: '--', unitWeight: '--', price: '0', minimumCharge: '0' },
      ];
    },
  },
  methods: {
    handleDownloadTemplate() {
      window.open(this.templateUrl, '_blank');
    },
    openImportDialog() {
      if (!this.importable) {
        this.$message.warning(this.$t(this.unavailableMessageKey));
        return;
      }
      this.importDialogVisible = true;
    },
    handleDialogClosed() {
      this.$emit('closed');
    },
    closeDialog() {
      this.importDialogVisible = false;
    },
    doLayout() {
      this.$refs.sampleTable && this.$refs.sampleTable.doLayout();
    },
  },
};
</script>

<style lang="scss" scoped>
.import-panel {
  display: grid;
  grid-template-columns: minmax(0, 3.5fr) minmax(0, 6.5fr);
  gap: 30px;
  min-width: 0;
  padding: 24px;
  background: #ffffff;
  border: 1px solid #e1e7ef;
  border-radius: 6px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.06);
}

.import-left,
.sample-box {
  min-width: 0;
}

.method-title {
  color: #1f2d3d;
  font-size: 15px;
  font-weight: 700;
}

.method-title span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  margin-right: 8px;
  color: #082dc3;
  background: #eef3ff;
  border-radius: 50%;
}

.sample-table {
  width: 100%;
}

.template-btn {
  color: #082dc3;
  border-color: #9cabe7;
}

::v-deep .sample-table th.el-table__cell {
  color: #1f2d3d;
  background: #f8fafc;
}

::v-deep .sample-table .cell {
  line-height: 1.35;
  white-space: normal;
  word-break: normal;
  overflow-wrap: normal;
}

.upload-actions {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-top: 22px;
}

.upload-actions .el-button {
  width: 100%;
  margin-left: 0;
}

.upload-btn-inline {
  background: #082dc3;
  border-color: #082dc3;
}

.upload-tip {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin: 2px 0 14px;
  color: #667085;
  font-size: 12px;
}

.sample-box h4 {
  margin: 0 0 10px;
  color: #1f2d3d;
}

@media (max-width: 1100px) {
  .import-panel {
    grid-template-columns: 1fr;
  }
}
</style>
